import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppLayout } from '@/components/layout/app-layout'
import { PublicLayout } from '@/components/layout/public-layout'
import { ErrorBoundary } from '@/components/error-boundary'
import { ErrorPage } from '@/components/error-page'
import { routes } from '@/lib/routes'
import MaintenancePage from '@/pages/MaintenancePage'

// Lazy loading das páginas
const HomePage = lazy(() => import('@/features/home/pages/home'))
const RollPage = lazy(() => import('@/features/roll/pages/roll'))
const SettingsPage = lazy(() => import('@/features/settings/pages/settings'))
const LoginPage = lazy(() => import('@/features/auth/pages/login'))
const RegisterPage = lazy(() => import('@/features/auth/pages/register'))
// Páginas de perfil (refatoradas)
const UserProfilePage = lazy(() => import('@/features/user-profile/pages/user-profile'))
const CollectorProfilePage = lazy(() => import('@/features/user-profile/pages/collector-profile'))
const InventoryPage = lazy(() => import('@/features/inventory/pages/inventory'))
const SeriesDetailsPage = lazy(() => import('@/features/inventory/pages/series-details'))
const TradeV2Page = lazy(() => import('@/features/trade/pages/trade-v2'))
const RebirthPage = lazy(() => import('@/features/rebirth/pages/rebirth'))
const CommunityPage = lazy(() => import('@/features/community/pages/community'))
const CollectorsPage = lazy(() => import('@/features/community/pages/collectors'))
const TermsAndPrivacyPage = lazy(() => import('@/features/legal/pages/terms-and-privacy'))
const DailyPage = lazy(() => import('@/features/daily/pages/daily'))
const WikiPage = lazy(() => import('@/features/wiki/pages/wiki'))
const BlackMarketPage = lazy(() => import('@/features/black-market/pages/black-market'))
const CommunityEventPage = lazy(() => import('@/features/community-event/pages/community-event'))
const StorePage = lazy(() => import('@/features/store/pages/store'))
const StoreMaintenancePage = lazy(() => import('@/features/store/pages/store-maintenance'))
const NotificationsPage = lazy(() => import('@/features/notifications/pages/notifications'))
const CardScannerPage = lazy(() => import('@/features/card-scanner/pages/card-scanner'))
const UnifiedMessagesPage = lazy(() => import('@/features/messages/pages/unified-messages'))
const RankingPage = lazy(() => import('@/features/ranking/pages/ranking'))

// Componente de Loading
function PageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-32 w-32 animate-pulse rounded-lg bg-accent/10" />
    </div>
  )
}

const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true'

export const router = createBrowserRouter(isMaintenanceMode
  ? [
      {
        path: '*',
        element: <MaintenancePage />,
      },
    ]
  : [
      {
        path: '/',
        element: <AppLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoading />}>
                <HomePage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'roll',
            element: (
              <Suspense fallback={<PageLoading />}>
                <RollPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'settings',
            element: (
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'profile',
            element: (
              <Suspense fallback={<PageLoading />}>
                <UserProfilePage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'profile/:username',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CollectorProfilePage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'inventory',
            element: (
              <Suspense fallback={<PageLoading />}>
                <InventoryPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'inventory/series/:seriesId',
            element: (
              <Suspense fallback={<PageLoading />}>
                <SeriesDetailsPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'trade',
            element: <Navigate to="/trade-v2" replace />,
          },
          {
            path: 'trade-v2',
            element: (
              <Suspense fallback={<PageLoading />}>
                <TradeV2Page />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'rebirth',
            element: (
              <Suspense fallback={<PageLoading />}>
                <RebirthPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'community',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CommunityPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'collectors',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CollectorsPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: 'daily',
            element: (
              <Suspense fallback={<PageLoading />}>
                <DailyPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: routes.wiki,
            element: <WikiPage />,
            errorElement: <ErrorPage />,
          },
          {
            path: routes.blackMarket,
            element: (
              <Suspense fallback={<PageLoading />}>
                <BlackMarketPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: routes.communityEvent,
            element: (
              <Suspense fallback={<PageLoading />}>
                <CommunityEventPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: routes.store,
            element: (
              <Suspense fallback={<PageLoading />}>
                <StorePage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: routes.notifications,
            element: (
              <Suspense fallback={<PageLoading />}>
                <NotificationsPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: routes.cardScanner,
            element: (
              <Suspense fallback={<PageLoading />}>
                <CardScannerPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
                {
                  path: routes.messages,
                  element: (
                    <Suspense fallback={<PageLoading />}>
                      <UnifiedMessagesPage />
                    </Suspense>
                  ),
                  errorElement: <ErrorPage />,
                },
          {
            path: routes.ranking,
            element: (
              <Suspense fallback={<PageLoading />}>
                <RankingPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          }
        ],
      },
      {
        path: '/',
        element: <PublicLayout />,
        children: [
          {
            path: 'login',
            element: (
              <Suspense fallback={<PageLoading />}>
                <LoginPage />
              </Suspense>
            ),
          },
          {
            path: 'register',
            element: (
              <Suspense fallback={<PageLoading />}>
                <RegisterPage />
              </Suspense>
            ),
          },
          {
            path: 'legal/terms-and-privacy',
            element: (
              <Suspense fallback={<PageLoading />}>
                <TermsAndPrivacyPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to={routes.home} replace />
      }
    ]) 